// ─────────────────────────────────────────────────────────────────────────
// CollabClient — thin WebSocket wrapper for ORACLE live moodboards (B17)
//
// Zero dependencies (native WebSocket) so it adds nothing to the bundle and
// stays clear of the Rollup module-init / TDZ traps noted in the handover.
//
// Handlers: onStatus, onWelcome, onSync, onOp, onCursor, onPresence
// ─────────────────────────────────────────────────────────────────────────

// Resolve the relay URL. In prod the same nginx that serves ORACLE proxies
// /collab → the relay container (see nginx.conf). In local dev we hit the
// relay directly on :3201.
export function collabUrl() {
  if (import.meta.env.VITE_COLLAB_URL) return import.meta.env.VITE_COLLAB_URL;
  const { protocol, hostname, host } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return "ws://localhost:3201/collab";
  const wsProto = protocol === "https:" ? "wss:" : "ws:";
  return `${wsProto}//${host}/collab`;
}

// Deterministic per-user colour from email/name so a person looks the same
// across sessions and to every teammate.
const PEER_COLORS = [
  "#E8FF47", "#5ED3F3", "#FF7AB2", "#7CF6A9",
  "#FFB155", "#C08BFF", "#FF6B6B", "#6BB8FF",
];
export function colorFor(key) {
  const s = String(key || "guest");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PEER_COLORS[h % PEER_COLORS.length];
}

// Web image search endpoint (B18). Same nginx that serves ORACLE proxies
// /api/search → the relay container; in local dev we hit the relay directly.
export function searchUrl(q, start = 1) {
  const qs = `?q=${encodeURIComponent(q)}&start=${start}`;
  if (import.meta.env.VITE_COLLAB_URL) {
    // dev override points at the ws relay; reuse its host for http
    try {
      const u = new URL(import.meta.env.VITE_COLLAB_URL.replace(/^ws/, "http"));
      return `${u.protocol}//${u.host}/api/search${qs}`;
    } catch { /* fall through */ }
  }
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return `http://localhost:3201/api/search${qs}`;
  return `/api/search${qs}`;
}

// Read-only daily search quota (B21) — same host resolution as searchUrl.
export function quotaUrl() {
  if (import.meta.env.VITE_COLLAB_URL) {
    try { const u = new URL(import.meta.env.VITE_COLLAB_URL.replace(/^ws/, "http")); return `${u.protocol}//${u.host}/api/search/quota`; } catch {}
  }
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return "http://localhost:3201/api/search/quota";
  return "/api/search/quota";
}

export function genRoomId() {
  const a = new Uint8Array(9);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return Array.from(a, b => "abcdefghijklmnopqrstuvwxyz0123456789"[b % 36]).join("");
}

export class CollabClient {
  constructor({ room, user, token = null, handlers = {} }) {
    this.room = room;
    this.user = user;
    this.token = token;
    this.h = handlers;
    this.ws = null;
    this.closed = false;
    this.retry = 0;
    this.selfId = null;
    this._cursorPending = null;
    this._cursorTimer = null;
  }

  connect() {
    this.closed = false;
    this._open();
    return this;
  }

  _open() {
    let ws;
    try { ws = new WebSocket(collabUrl()); }
    catch (e) { this._scheduleReconnect(); return; }
    this.ws = ws;
    this.h.onStatus?.("connecting");

    ws.onopen = () => {
      this.retry = 0;
      this.h.onStatus?.("connected");
      this._send({ t: "hello", room: this.room, user: this.user, token: this.token });
    };

    ws.onmessage = (ev) => {
      let m; try { m = JSON.parse(ev.data); } catch { return; }
      switch (m.t) {
        case "welcome":
          this.selfId = m.selfId;
          this.h.onWelcome?.(m);
          break;
        case "sync":     this.h.onSync?.(m.state); break;
        case "op":       this.h.onOp?.(m.op, m.from); break;
        case "cursor":   this.h.onCursor?.(m.from, m.x, m.y); break;
        case "presence": this.h.onPresence?.(m.peers); break;
        case "denied":
          this.closed = true;                 // do not auto-reconnect on auth failure
          this.h.onStatus?.("denied");
          this.h.onDenied?.(m.reason);
          try { this.ws?.close(); } catch {}
          break;
        default: break;
      }
    };

    ws.onclose = () => {
      if (this.closed) { this.h.onStatus?.("closed"); return; }
      this.h.onStatus?.("reconnecting");
      this._scheduleReconnect();
    };

    ws.onerror = () => { try { ws.close(); } catch {} };
  }

  _scheduleReconnect() {
    if (this.closed) return;
    this.retry = Math.min(this.retry + 1, 6);
    const delay = Math.min(800 * 2 ** (this.retry - 1), 8000);
    setTimeout(() => { if (!this.closed) this._open(); }, delay);
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(obj)); return true; } catch {}
    }
    return false;
  }

  // Push the whole local board to seed a fresh room (host on Go Live).
  seed(state) { this._send({ t: "state", state }); }

  // Send a granular op (add/update/remove/clear/links/zoom/offset/canvas).
  op(op) { this._send({ t: "op", op }); }

  // Cursor position in canvas-space, throttled to ~30Hz.
  cursor(x, y) {
    this._cursorPending = { x, y };
    if (this._cursorTimer) return;
    this._cursorTimer = setTimeout(() => {
      this._cursorTimer = null;
      if (this._cursorPending) {
        this._send({ t: "cursor", x: this._cursorPending.x, y: this._cursorPending.y });
        this._cursorPending = null;
      }
    }, 33);
  }

  close() {
    this.closed = true;
    clearTimeout(this._cursorTimer);
    try { this._send({ t: "bye" }); } catch {}
    try { this.ws?.close(); } catch {}
  }
}
